import { c as _c } from "react/compiler-runtime";
import { useQuery, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import type * as fetch from 'shared/api/fetch';
import * as analytics from 'shared/api/analytics';
import * as standardReports from 'shared/api/standardReports';
import { type CurrencyCodes } from 'shared/constants/currency';
import { queryKeys as reportingSolutionsQueryKeys } from 'reportingSolution/api';
import useMutationAnalytics from 'shared/hooks/useMutationAnalytics';
export interface ConfigReq {
  id: string;
  targetCurrencyCode: CurrencyCodes;
  report: analytics.ReportType;
  clientId: number;
  filters?: analytics.Filter[];
  fields?: analytics.AnalyticsReqField[];
  groupBy?: string[];
  orderBy?: analytics.AnalyticsReqOrderBy;
}
export const channelGroupingReportAPIVariables = {
  reportAnalyticsVariables: {
    fields: [{
      name: 'transaction_revenue',
      aggregation: 'SUM'
    }, {
      name: 'transactions',
      aggregation: 'SUM'
    }, {
      name: 'ad_cost',
      aggregation: 'SUM'
    }, {
      name: 'date'
    }, {
      name: 'market_channel_grouping'
    }, {
      name: 'custom_channel_grouping'
    }],
    groupBy: ['date', 'market_channel_grouping', 'custom_channel_grouping']
  },
  marketFieldVariableName: 'market_channel_grouping',
  channelFieldVariableName: 'custom_channel_grouping',
  totalsVariables: {
    fields: [{
      name: 'transaction_revenue',
      aggregation: 'SUM'
    }, {
      name: 'transactions',
      aggregation: 'SUM'
    }, {
      name: 'ad_cost',
      aggregation: 'SUM'
    }]
  },
  filterVariables: {
    fields: [{
      name: 'market_channel_grouping'
    }, {
      name: 'custom_channel_grouping'
    }],
    groupBy: ['market_channel_grouping', 'custom_channel_grouping']
  },
  customDataFilters: [{
    field: 'is_combined_data',
    operator: '=',
    value: true
  }]
};
export const reportingBaseAPIVariables = {
  reportAnalyticsVariables: {
    fields: [{
      name: 'conversion_value',
      aggregation: 'SUM'
    }, {
      name: 'conversions',
      aggregation: 'SUM'
    }, {
      name: 'cost',
      aggregation: 'SUM'
    }, {
      name: 'date'
    }, {
      name: 'market_name'
    }, {
      name: 'channel_grouping'
    }, {
      name: 'video_views',
      aggregation: 'SUM'
    }, {
      name: 'impressions',
      aggregation: 'SUM'
    }, {
      name: 'clicks',
      aggregation: 'SUM'
    }, {
      name: 'viewable_impressions',
      aggregation: 'SUM'
    }, {
      name: 'sessions',
      aggregation: 'SUM'
    }, {
      name: 'daily_target',
      aggregation: 'SUM'
    }, {
      name: 'daily_budget',
      aggregation: 'SUM'
    }],
    groupBy: ['date', 'market_name', 'channel_grouping']
  },
  marketFieldVariableName: 'market_name',
  channelFieldVariableName: 'channel_grouping',
  totalsVariables: {
    fields: [{
      name: 'conversion_value',
      aggregation: 'SUM'
    }, {
      name: 'conversions',
      aggregation: 'SUM'
    }, {
      name: 'cost',
      aggregation: 'SUM'
    }, {
      name: 'video_views',
      aggregation: 'SUM'
    }, {
      name: 'impressions',
      aggregation: 'SUM'
    }, {
      name: 'clicks',
      aggregation: 'SUM'
    }, {
      name: 'viewable_impressions',
      aggregation: 'SUM'
    }, {
      name: 'sessions',
      aggregation: 'SUM'
    }, {
      name: 'daily_target',
      aggregation: 'SUM'
    }, {
      name: 'daily_budget',
      aggregation: 'SUM'
    }]
  },
  filterVariables: {
    fields: [{
      name: 'market_name'
    }, {
      name: 'channel_grouping'
    }],
    groupBy: ['market_name', 'channel_grouping']
  },
  conversionEventVariables: {
    fields: [{
      name: 'conversion_name'
    }, {
      name: 'conversion_source'
    }, {
      name: 'conversions',
      aggregation: 'SUM'
    }],
    groupBy: ['conversion_name', 'conversion_source'],
    orderBy: {
      fields: ['conversions'],
      direction: 'DESC'
    }
  }
};
export const useMutationReportAnalytics = () => {
  return useMutationAnalytics(_temp);
};
export const useQueryStandardReports = clientId => {
  const $ = _c(7);
  let t0;
  let t1;
  if ($[0] !== clientId) {
    t0 = [reportingSolutionsQueryKeys.reportingSolutionsConfigs, clientId];
    t1 = async () => await standardReports.getStandardReports(clientId);
    $[0] = clientId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = Boolean(clientId);
  let t3;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useQuery(t3);
};
export const useMutationStandardReportStatuses = () => {
  return useMutationAnalytics(_temp2);
};
async function _temp(variables) {
  return await analytics.postReport({
    collection: "standard_reports",
    ...variables
  });
}
async function _temp2(clientId) {
  return await analytics.postConfigStatuses("transform_client_configs_status_ui", clientId, true);
}